// NEWS2 — National Early Warning Score 2 (RCP UK 2017).
// Segmented-control UI: each button's background reflects the point value
// (0 gray/white, 1 yellow, 2 orange, 3 red) so the clinician can eyeball
// the physiological derangement at a glance.

import React, { useState, useMemo } from "react";
import { AlertTriangle, ShieldAlert, ThermometerSun } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";
import { cn } from "@/lib/utils";

const SCORE_STYLE = {
  0: {
    idle: "border-stone-200 bg-white text-stone-600 hover:bg-stone-50",
    active: "border-stone-500 bg-stone-100 text-stone-900 shadow-inner",
  },
  1: {
    idle: "border-yellow-200 bg-yellow-50 text-yellow-800 hover:bg-yellow-100",
    active: "border-yellow-500 bg-yellow-100 text-yellow-900 shadow-inner",
  },
  2: {
    idle: "border-orange-200 bg-orange-50 text-orange-800 hover:bg-orange-100",
    active: "border-orange-500 bg-orange-200 text-orange-900 shadow-inner",
  },
  3: {
    idle: "border-red-300 bg-red-50 text-red-700 hover:bg-red-100",
    active: "border-red-600 bg-red-200 text-red-900 shadow-inner",
  },
};

// Standard NEWS2 ranges (RCP 2017).
const RR = [
  { label: "≤ 8", score: 3 },
  { label: "9 – 11", score: 1 },
  { label: "12 – 20", score: 0 },
  { label: "21 – 24", score: 2 },
  { label: "≥ 25", score: 3 },
];

const SPO2_S1 = [
  { label: "≤ 91", score: 3 },
  { label: "92 – 93", score: 2 },
  { label: "94 – 95", score: 1 },
  { label: "≥ 96", score: 0 },
];

// Scale 2 for hypercapnic respiratory failure (target 88–92%).
const SPO2_S2 = [
  { label: "≤ 83", score: 3 },
  { label: "84 – 85", score: 2 },
  { label: "86 – 87", score: 1 },
  { label: "88 – 92 (air)", score: 0 },
  { label: "93 – 94 (O₂)", score: 1 },
  { label: "95 – 96 (O₂)", score: 2 },
  { label: "≥ 97 (O₂)", score: 3 },
];

const OXYGEN = [
  { labelEn: "Room air", labelId: "Udara ruang", score: 0 },
  { labelEn: "On oxygen", labelId: "Dengan oksigen", score: 2 },
];

const TEMP = [
  { label: "≤ 35.0", score: 3 },
  { label: "35.1 – 36.0", score: 1 },
  { label: "36.1 – 38.0", score: 0 },
  { label: "38.1 – 39.0", score: 1 },
  { label: "≥ 39.1", score: 2 },
];

const SBP = [
  { label: "≤ 90", score: 3 },
  { label: "91 – 100", score: 2 },
  { label: "101 – 110", score: 1 },
  { label: "111 – 219", score: 0 },
  { label: "≥ 220", score: 3 },
];

const HR = [
  { label: "≤ 40", score: 3 },
  { label: "41 – 50", score: 1 },
  { label: "51 – 90", score: 0 },
  { label: "91 – 110", score: 1 },
  { label: "111 – 130", score: 2 },
  { label: "≥ 131", score: 3 },
];

const ACVPU = [
  { key: "A", labelEn: "Alert", labelId: "Sadar (A)", score: 0 },
  { key: "C", labelEn: "New confusion", labelId: "Bingung baru (C)", score: 3 },
  { key: "V", labelEn: "Voice", labelId: "Respons suara (V)", score: 3 },
  { key: "P", labelEn: "Pain", labelId: "Respons nyeri (P)", score: 3 },
  { key: "U", labelEn: "Unresponsive", labelId: "Tidak respons (U)", score: 3 },
];

function Segment({ options, value, onChange, testid, labelKey = "label", getLabel }) {
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testid}>
      {options.map((opt, i) => {
        const active = value === i;
        const style = SCORE_STYLE[opt.score];
        const label = getLabel ? getLabel(opt) : opt[labelKey];
        return (
          <button
            key={i}
            type="button"
            data-testid={`${testid}-opt-${i}`}
            aria-pressed={active}
            onClick={() => onChange(i)}
            className={cn(
              "border-2 rounded-md px-3 py-2 min-h-[44px] text-xs md:text-sm font-medium transition-colors flex items-center gap-2",
              active ? style.active : style.idle
            )}
          >
            <span>{label}</span>
            <span
              className={cn(
                "inline-flex items-center justify-center h-5 w-5 rounded-full text-[10px] font-mono font-bold border",
                opt.score === 0
                  ? "bg-white border-stone-300 text-stone-500"
                  : opt.score === 1
                  ? "bg-yellow-200 border-yellow-500 text-yellow-900"
                  : opt.score === 2
                  ? "bg-orange-300 border-orange-500 text-orange-900"
                  : "bg-red-500 border-red-700 text-white"
              )}
            >
              {opt.score}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function Field({ title, hint, children }) {
  return (
    <div className="border border-stone-200 bg-white rounded-md p-4 md:p-5">
      <div className="flex items-baseline justify-between mb-3 flex-wrap gap-1">
        <div className="font-display text-base font-medium text-stone-900">{title}</div>
        {hint && <div className="text-xs text-stone-500 font-mono">{hint}</div>}
      </div>
      {children}
    </div>
  );
}

export default function News() {
  const { lang } = useLanguage();
  const isID = lang === "id";

  const [rr, setRR] = useState(null);
  const [spo2, setSpo2] = useState(null);
  const [scale, setScale] = useState(1); // 1 or 2
  const [ox, setOx] = useState(0); // 0 idx = room air, 1 idx = on oxygen
  const [temp, setTemp] = useState(null);
  const [sbp, setSbp] = useState(null);
  const [hr, setHr] = useState(null);
  const [acvpu, setAcvpu] = useState(null);

  const spo2Range = scale === 1 ? SPO2_S1 : SPO2_S2;

  const scores = [
    rr !== null && RR[rr].score,
    spo2 !== null && spo2Range[spo2].score,
    OXYGEN[ox].score,
    temp !== null && TEMP[temp].score,
    sbp !== null && SBP[sbp].score,
    hr !== null && HR[hr].score,
    acvpu !== null && ACVPU[acvpu].score,
  ].filter((v) => v !== false && v !== null);

  const total = scores.reduce((a, b) => a + b, 0);
  const anyRedFlag = [
    rr !== null && RR[rr].score === 3,
    spo2 !== null && spo2Range[spo2].score === 3,
    temp !== null && TEMP[temp].score === 3,
    sbp !== null && SBP[sbp].score === 3,
    hr !== null && HR[hr].score === 3,
    acvpu !== null && ACVPU[acvpu].score === 3,
  ].some(Boolean);

  const risk = useMemo(() => {
    if (total >= 7) return { level: "high", color: "red" };
    if (total >= 5) return { level: "medium", color: "orange" };
    if (anyRedFlag) return { level: "medium", color: "orange", redFlag: true };
    if (total >= 1) return { level: "low", color: "yellow" };
    return { level: "none", color: "stone" };
  }, [total, anyRedFlag]);

  const response = useMemo(() => {
    switch (risk.level) {
      case "high":
        return isID
          ? "Aktivasi tim emergensi/kritis. Perawatan level 2/3, monitor kontinu, evaluasi klinis segera oleh tim yang mampu resusitasi lanjut."
          : "Emergency/critical care team activation. Level 2/3 care, continuous monitoring, immediate clinical review by team competent in advanced resuscitation.";
      case "medium":
        return isID
          ? "Evaluasi urgent oleh klinisi mampu menangani pasien akut, pertimbangkan peningkatan level perawatan, monitor minimum tiap 1 jam."
          : "Urgent review by a clinician competent in acute care, consider escalation of care, minimum hourly monitoring.";
      case "low":
        return isID
          ? "Perawat menilai ulang, tentukan frekuensi pemantauan lanjut (minimum tiap 4–6 jam)."
          : "Registered nurse review, decide onward monitoring frequency (minimum every 4–6 h).";
      default:
        return isID
          ? "Lanjutkan pemantauan rutin sesuai protokol RS."
          : "Continue routine monitoring per hospital protocol.";
    }
  }, [risk.level, isID]);

  const riskColor =
    risk.color === "red"
      ? "border-red-600 bg-red-50 text-red-900"
      : risk.color === "orange"
      ? "border-orange-600 bg-orange-50 text-orange-900"
      : risk.color === "yellow"
      ? "border-yellow-500 bg-yellow-50 text-yellow-900"
      : "border-stone-300 bg-white text-stone-800";
  const riskPill =
    risk.color === "red"
      ? "bg-red-600 text-white"
      : risk.color === "orange"
      ? "bg-orange-600 text-white"
      : risk.color === "yellow"
      ? "bg-yellow-400 text-yellow-900"
      : "bg-stone-100 text-stone-700";

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-10 py-8 md:py-12" data-testid="news-page">
      <header className="border-b border-stone-200 pb-5 mb-8">
        <div className="text-[11px] tracking-[0.18em] uppercase font-semibold text-sky-700 mb-2 flex items-center gap-2">
          <ThermometerSun size={14} strokeWidth={1.5} />
          {isID ? "Peringatan Dini · Tanda Vital" : "Early Warning · Vital Signs"}
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-medium text-stone-900 tracking-tight leading-tight">
          NEWS2
        </h1>
        <p className="mt-3 text-sm md:text-base text-stone-600 leading-relaxed max-w-2xl">
          {isID
            ? "Skor peringatan dini nasional (RCP UK 2017). Nilai 7 parameter fisiologis untuk mendeteksi perburukan pasien dewasa."
            : "National Early Warning Score (RCP UK 2017). Rate 7 physiological parameters to detect deterioration in adult patients."}
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-6 lg:gap-8 items-start">
        {/* LEFT — form fields */}
        <div className="space-y-4" data-testid="news-form">
        <Field title={isID ? "Laju napas" : "Respiratory rate"} hint="breaths / min">
          <Segment options={RR} value={rr} onChange={setRR} testid="news-rr" />
        </Field>

        <Field title={isID ? "SpO₂" : "SpO₂"} hint="%">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <span className="text-xs font-mono text-stone-500 uppercase tracking-wider">
              {isID ? "Skala" : "Scale"}
            </span>
            {[1, 2].map((n) => (
              <button
                key={n}
                type="button"
                data-testid={`news-scale-${n}`}
                onClick={() => {
                  setScale(n);
                  setSpo2(null);
                }}
                aria-pressed={scale === n}
                className={cn(
                  "px-3 py-1.5 rounded border text-xs font-medium transition-colors",
                  scale === n ? "bg-sky-700 border-sky-700 text-white" : "border-stone-300 bg-white text-stone-600 hover:bg-stone-50"
                )}
              >
                {n === 1 ? (isID ? "1 · Umum" : "1 · General") : isID ? "2 · Hiperkapnik" : "2 · Hypercapnic"}
              </button>
            ))}
          </div>
          <Segment options={spo2Range} value={spo2} onChange={setSpo2} testid="news-spo2" />
        </Field>

        <Field title={isID ? "Penggunaan oksigen" : "Oxygen use"}>
          <Segment
            options={OXYGEN}
            value={ox}
            onChange={setOx}
            testid="news-oxygen"
            getLabel={(o) => (isID ? o.labelId : o.labelEn)}
          />
        </Field>

        <Field title={isID ? "Suhu tubuh" : "Temperature"} hint="°C">
          <Segment options={TEMP} value={temp} onChange={setTemp} testid="news-temp" />
        </Field>

        <Field title={isID ? "Tekanan darah sistolik" : "Systolic BP"} hint="mmHg">
          <Segment options={SBP} value={sbp} onChange={setSbp} testid="news-sbp" />
        </Field>

        <Field title={isID ? "Nadi" : "Heart rate"} hint="bpm">
          <Segment options={HR} value={hr} onChange={setHr} testid="news-hr" />
        </Field>

        <Field title={isID ? "Kesadaran (ACVPU)" : "Consciousness (ACVPU)"}>
          <Segment
            options={ACVPU}
            value={acvpu}
            onChange={setAcvpu}
            testid="news-acvpu"
            getLabel={(o) => (isID ? o.labelId : o.labelEn)}
          />
        </Field>
      </div>

      {/* RIGHT column — result card. Sticky on desktop; on mobile it flows
          naturally at the bottom (no fixed positioning). */}
      <div
        className="lg:sticky lg:top-6"
        data-testid="news-result"
      >
        <div className={cn("border-2 rounded-lg p-4 shadow-lg flex items-start gap-4", riskColor)}>
          <div className={cn("h-14 w-14 rounded-full flex items-center justify-center font-display text-2xl font-medium shrink-0", riskPill)}>
            {total}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] tracking-[0.18em] uppercase font-semibold opacity-70">
              {isID ? "Total NEWS2" : "Total NEWS2"}
              {risk.redFlag && (
                <span className="ml-2 inline-flex items-center gap-1 text-red-700 font-semibold normal-case tracking-normal">
                  <AlertTriangle size={11} strokeWidth={2} /> {isID ? "1 parameter = 3" : "1 param = 3"}
                </span>
              )}
            </div>
            <div className="font-display text-lg font-medium">
              {risk.level === "high"
                ? isID
                  ? "Risiko tinggi"
                  : "High risk"
                : risk.level === "medium"
                ? isID
                  ? "Risiko sedang"
                  : "Medium risk"
                : risk.level === "low"
                ? isID
                  ? "Risiko rendah"
                  : "Low risk"
                : isID
                ? "Belum ada input"
                : "No inputs yet"}
            </div>
            <p className="text-xs leading-relaxed opacity-90 mt-1">{response}</p>
          </div>
          {risk.level === "high" && <ShieldAlert size={26} strokeWidth={1.5} className="shrink-0" />}
        </div>
      </div>
      </div>

      <div className="mt-10 max-w-2xl">
        <div className="border-l-2 border-amber-700/60 pl-4 py-1 text-xs text-stone-600 leading-relaxed">
          <span className="font-semibold text-stone-900">{isID ? "Sebagai rujukan saja." : "Reference only."}</span>{" "}
          {isID
            ? "NEWS2 divalidasi untuk pasien dewasa (≥ 16 th), bukan untuk kehamilan atau pasien dengan hipoksemia kronis stabil tanpa target 88–92%. Setiap parameter dengan skor 3 memicu eskalasi meski total < 5."
            : "NEWS2 is validated for adults (≥ 16 y), not pregnancy or stable chronic hypoxaemia without an 88–92% target. Any single parameter scoring 3 triggers escalation even if total < 5."}
        </div>
      </div>
    </div>
  );
}
